/**
 * Care-level display metadata for CareStart triage results.
 *
 * WHAT: maps a resolved careLevel ("ER" | "UrgentCare" | "PrimaryCare" | "Specialist")
 *   to the patient-facing headline, subhead, MUI color, and icon shown in the care-level
 *   badge ("Consider an Emergency visit.", etc.).
 * WHY: this copy/color set is now rendered in two places — the right-column CareLevelBadge
 *   (anonymous live flow) and TriageResultPanel (authenticated post-auth restore flow).
 *   Keeping it here prevents the two from drifting apart.
 *
 * Tightest coupling: CareLevelBadge.jsx and TriageResultPanel.jsx both key into this by the
 *   careLevel string produced by deriveCareGuidance.resolveCareLevel.
 */
import i18n from "i18n";

// label/sublabel are getters so every read (here and in SharedRecordSummary) is in the
// current language; the keys and muiColor stay fixed.
const display = (level, muiColor) =>
  Object.freeze({
    get label() {
      return i18n.t(`careStart:careLevel.${level}.label`);
    },
    get sublabel() {
      return i18n.t(`careStart:careLevel.${level}.sublabel`);
    },
    muiColor,
  });

export const CARE_LEVEL_DISPLAY = Object.freeze({
  ER: display("ER", "error"),
  UrgentCare: display("UrgentCare", "warning"),
  PrimaryCare: display("PrimaryCare", "success"),
  Specialist: display("Specialist", "info"),
});

export default CARE_LEVEL_DISPLAY;

/**
 * The care-level guidance sentence to show the patient, in the current language.
 *
 * resolveCareLevel stores either the triage model's own sentence or a fixed English fallback
 * (deriveCareGuidance ACTION_GUIDANCE); that stored value is what Firestore, the provider share and
 * the ChartMind prompt see, so it stays English. Only the fixed fallback is swapped for its
 * translation here, at render; a model-written sentence is shown as written.
 *
 * @param {string|null} careLevel
 * @param {string|null} actionGuidance - Stored guidance sentence.
 * @returns {string|null}
 */
export function displayActionGuidance(careLevel, actionGuidance) {
  if (!careLevel || !actionGuidance) return actionGuidance;
  const key = `careStart:actionGuidance.${careLevel}`;
  if (!i18n.exists(key)) return actionGuidance;
  return actionGuidance.trim() === i18n.t(key, { lng: "en" }) ? i18n.t(key) : actionGuidance;
}
